/**
 * <s-image-lightbox> — shared full-screen viewer for editorial images.
 *
 * Any [data-lightbox-item] on the page (inside a [data-lightbox-group]) opens
 * the single dialog this element wraps. The viewer reads the clicked <img>, so
 * it always shows whatever image the merchant has picked for that block, and
 * requests a larger Shopify CDN rendition of it.
 *
 * Required markup inside the element:
 *   <s-dialog><dialog> … img[data-lightbox-image] …
 *   [data-lightbox-prev] / [data-lightbox-next] buttons (hidden for single images)
 *   [data-lightbox-counter] (optional)
 */
const ZOOM_WIDTHS = [750, 1100, 1500, 2000];

function resize(url, width) {
  try {
    const u = new URL(url, location.href);
    u.searchParams.set('width', String(width));
    u.searchParams.delete('height');
    u.searchParams.delete('crop');
    return u.toString();
  } catch {
    return url;
  }
}

class SImageLightbox extends HTMLElement {
  #ac;
  #dialog;
  #img;
  #items = [];
  #index = 0;
  #touchX = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#img = this.querySelector('img[data-lightbox-image]');
    if (!this.#dialog || !this.#img) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#enhanceItems();

    document.addEventListener('click', this.#onClick, opts);
    document.addEventListener('keydown', this.#onItemKey, opts);
    this.#dialog.addEventListener('keydown', this.#onDialogKey, opts);
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.#step(-1), opts);
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.#step(1), opts);
    this.#dialog.addEventListener('touchstart', (e) => { this.#touchX = e.touches[0]?.clientX ?? null; }, { ...opts, passive: true });
    this.#dialog.addEventListener('touchend', (e) => {
      if (this.#touchX === null || this.#items.length < 2) return;
      const dx = (e.changedTouches[0]?.clientX ?? this.#touchX) - this.#touchX;
      this.#touchX = null;
      if (Math.abs(dx) > 50) this.#step(dx < 0 ? 1 : -1);
    }, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #enhanceItems() {
    const label = this.dataset.itemLabel || 'View larger image';
    for (const item of document.querySelectorAll('[data-lightbox-item]')) {
      if (item.hasAttribute('role')) continue;
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-haspopup', 'dialog');
      if (!item.hasAttribute('aria-label')) item.setAttribute('aria-label', label);
    }
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('[data-lightbox-item]');
    if (!item || item.closest('dialog')) return;
    event.preventDefault();
    this.#open(item);
  };

  #onItemKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element) || !event.target.matches('[data-lightbox-item]')) return;
    event.preventDefault();
    this.#open(event.target);
  };

  #onDialogKey = (event) => {
    if (event.key === 'ArrowRight') this.#step(document.dir === 'rtl' ? -1 : 1);
    if (event.key === 'ArrowLeft') this.#step(document.dir === 'rtl' ? 1 : -1);
  };

  #open(item) {
    const group = item.closest('[data-lightbox-group]');
    this.#items = [...(group || document).querySelectorAll('[data-lightbox-item]')].filter((el) => el.querySelector('img'));
    this.#index = Math.max(0, this.#items.indexOf(item));
    const multi = this.#items.length > 1;
    this.querySelectorAll('[data-lightbox-prev], [data-lightbox-next], [data-lightbox-counter]').forEach((el) => { el.hidden = !multi; });
    if (!this.#show()) return;
    if (!this.#dialog.open) this.#dialog.showModal();
  }

  #step(delta) {
    if (this.#items.length < 2) return;
    this.#index = (this.#index + delta + this.#items.length) % this.#items.length;
    this.#show();
  }

  #show() {
    const source = this.#items[this.#index]?.querySelector('img');
    if (!source) return false;
    const base = source.currentSrc || source.src;
    if (!base) return false;
    this.#img.srcset = ZOOM_WIDTHS.map((w) => `${resize(base, w)} ${w}w`).join(', ');
    this.#img.sizes = '100vw';
    this.#img.src = resize(base, 1500);
    this.#img.alt = source.alt || '';
    const counter = this.querySelector('[data-lightbox-counter]');
    if (counter) counter.textContent = `${this.#index + 1} / ${this.#items.length}`;
    return true;
  }
}

if (!customElements.get('s-image-lightbox')) {
  customElements.define('s-image-lightbox', SImageLightbox);
}
